import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { mapsAPI } from '../services/api';
import { useAuth } from './AuthContext';

const MapsContext = createContext();

export const useMaps = () => {
  const context = useContext(MapsContext);
  if (!context) {
    throw new Error('useMaps must be used within a MapsProvider');
  }
  return context;
};

export const MapsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalMaps: 0,
    activeMaps: 0,
    totalVisits: 0,
    totalMarkers: 0
  });

  const fetchMaps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching user maps...');
      const response = await mapsAPI.getMaps();
      const mapsData = response.data.data.maps;
      
      console.log('✅ Maps fetched:', mapsData);
      setMaps(mapsData);
      
      // Calculate stats
      const totalMaps = mapsData.length;
      const activeMaps = mapsData.filter(map => map.isCurrentlyActive).length;
      const totalVisits = mapsData.reduce((sum, map) => sum + (map.visitCount || 0), 0);
      const totalMarkers = mapsData.reduce((sum, map) => sum + (map.markersCount || 0), 0);
      
      setStats({
        totalMaps,
        activeMaps,
        totalVisits,
        totalMarkers
      });
      
    } catch (error) {
      console.error('❌ Error fetching maps:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch maps';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteMap = useCallback(async (mapId) => {
    try {
      setLoading(true);
      await mapsAPI.deleteMap(mapId);
      
      // Remove from local state
      setMaps(prevMaps => prevMaps.filter(map => map.id !== mapId));
      
      console.log('✅ Map deleted:', mapId);
      
      // Refresh stats
      await fetchMaps();
      
    } catch (error) {
      console.error('❌ Error deleting map:', error);
      setError(error.response?.data?.message || 'Failed to delete map');
    } finally {
      setLoading(false);
    }
  }, [fetchMaps]);

  const refreshMaps = useCallback(() => {
    fetchMaps();
  }, [fetchMaps]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch user's maps when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchMaps();
    } else {
      // Clear data when logged out
      setMaps([]);
      setStats({
        totalMaps: 0,
        activeMaps: 0,
        totalVisits: 0,
        totalMarkers: 0
      });
    }
  }, [isAuthenticated, fetchMaps]);

  const value = {
    maps,
    loading,
    error,
    stats,
    fetchMaps,
    deleteMap,
    refreshMaps,
    clearError
  };

  return (
    <MapsContext.Provider value={value}>
      {children}
    </MapsContext.Provider>
  );
};